import React, { ChangeEvent, useEffect, useRef, useState } from 'react';
import { useLocalization } from '@ais-ui/locale-management';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
  InfoIconTooltip,
  Input,
  ScrollArea,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useToast,
} from '@ais-ui/ui-core';
import { mutateCallbacks } from '../../utils/mutateCallbacks';
import { useShapesImport } from '@ais-ui/api';
import { z } from 'zod';
import { useForm, UseFormReturn } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { GroupSelect } from '../GroupSelect';
import { PiUploadThin } from 'react-icons/pi';

const importShapeSchema = z.object({
  name: z.string().min(1),
  importType: z.string(),
  parentId: z.string(),
  importData: z.union([z.instanceof(File), z.string()]),
});

interface ImportShapeValues {
  name: string;
  importType: string;
  parentId: string;
  importData: File | string;
}

interface ImportShapeDialogProps {
  showDialog: boolean;
  setShowDialog: React.Dispatch<React.SetStateAction<boolean>>;
}

interface ImportShapeFormProps {
  form: UseFormReturn<ImportShapeValues>;
}

function readFileAsBase64(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();

    reader.onloadend = () => {
      const arrayBuffer = reader.result as ArrayBuffer;
      const base64 = btoa(
        new Uint8Array(arrayBuffer).reduce(
          (data, byte) => data + String.fromCharCode(byte),
          ''
        )
      );
      resolve(base64);
    };
    reader.onerror = reject;

    reader.readAsArrayBuffer(file);
  });
}

interface ImportDataFormFieldProps {
  form: UseFormReturn<ImportShapeValues>;
}

const ImportDataFormField = ({ form }: ImportDataFormFieldProps) => {
  const { messages } = useLocalization();

  const geoJsonFileInputRef = useRef<HTMLInputElement>(null);
  const wktFileInputRef = useRef<HTMLInputElement>(null);
  const kmlFileInputRef = useRef<HTMLInputElement>(null);

  const onChooseGeoJsonFileClick = () => {
    if (geoJsonFileInputRef.current) {
      geoJsonFileInputRef.current.click();
    }
  };

  const onChooseWKTFileClick = () => {
    if (wktFileInputRef.current) {
      wktFileInputRef.current.click();
    }
  };

  const onChooseKMLFileClick = () => {
    if (kmlFileInputRef.current) {
      kmlFileInputRef.current.click();
    }
  };

  const onFileChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files) {
      const file = event.target.files[0];
      const reader = new FileReader();
      reader.onload = (e: ProgressEvent<FileReader>) => {
        if (!e.target?.result) {
          return;
        }

        const textData = (e.target.result as string).trim();

        form.setValue('importData', textData);
      };

      reader.onloadend = () => {
        // Reset the value of the input to allow the same file to be selected again
        event.target.value = '';
      };

      reader.readAsText(file);
    }
  };

  const geoJsonPlaceholder = `{
  "type": "Polygon",
  "coordinates": [
    [
      [10.5, 57.75],
      [10.6471, 57.75],
      [10.6471, 57.8401],
      [10.5, 57.8401],
      [10.5, 57.75]
    ]
  ]
}`;

  const kmlPlaceholder = `<?xml version="1.0" encoding="UTF-8"?>
<kml xmlns="http://www.opengis.net/kml/2.2">
  <Document>
    <name>Sample Polygon</name>
    <Placemark>
      <name>Sample Polygon</name>
      <Polygon>
        <outerBoundaryIs>
          <LinearRing>
            <coordinates>
              12.5500,55.6800,0
              12.5600,55.6800,0
              12.5600,55.6850,0
              12.5500,55.6850,0
              12.5500,55.6800,0
            </coordinates>
          </LinearRing>
        </outerBoundaryIs>
      </Polygon>
    </Placemark>
  </Document>
</kml>`;

  const [importType, setImportType] = useState(form.getValues('importType'));
  useEffect(() => {
    const subscription = form.watch((value, { name }) => {
      if (name === 'importType') {
        setImportType(value.importType as string);
        form.resetField('importData');
      }
    });

    return () => subscription.unsubscribe();
  }, [form]);

  return (
    <div className="space-y-8">
      <FormField
        control={form.control}
        name="importType"
        render={({ field }) => (
          <FormItem>
            <div className="flex justify-between">
              <FormLabel>{messages.shapeImportImportTypeLabel} *</FormLabel>
            </div>
            <FormControl onChange={() => form.clearErrors()}>
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select import type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="shapefile">
                    {messages.shapeImportShapeSelectShapefile}
                  </SelectItem>
                  <SelectItem value="geojson">
                    {messages.shapeImportShapeSelectGeojson}
                  </SelectItem>
                  <SelectItem value="wkt">
                    {messages.shapeImportShapeSelectWKT}
                  </SelectItem>
                  <SelectItem value="kml">KML</SelectItem>
                </SelectContent>
              </Select>
            </FormControl>
            <FormMessage />
          </FormItem>
        )}
      />
      {importType === 'shapefile' && (
        <FormField
          control={form.control}
          name="importData"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>{messages.shapeImportShapefileLabel} *</FormLabel>
              </div>
              <FormControl
                onChange={(e) => {
                  form.clearErrors();
                  const target = e.target as HTMLInputElement;
                  if (target.files) {
                    field.onChange(target.files[0]); // set the File object to the body field
                  }
                }}
              >
                <Input id="file" type="file" accept=".zip" />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {importType === 'geojson' && (
        <FormField
          control={form.control}
          name="importData"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FormLabel>{messages.shapeImportGeoJsonLabel} * </FormLabel>
                  <div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="flex space-x-2"
                          onClick={onChooseGeoJsonFileClick}
                        >
                          <PiUploadThin className="h-4 w-4"></PiUploadThin>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {messages.shapeImportGeoJsonFile}
                      </TooltipContent>
                    </Tooltip>
                    <Input
                      ref={geoJsonFileInputRef}
                      type="file"
                      accept=".geojson"
                      className="hidden"
                      onChange={onFileChange}
                    ></Input>
                  </div>
                </div>
                <InfoIconTooltip>
                  Import GeoJSON via file or write it in plain text
                </InfoIconTooltip>
              </div>
              <FormControl
                onChange={(e) => {
                  form.clearErrors();
                  const target = e.target as HTMLTextAreaElement;
                  field.onChange(target.value); // set the WKT string to the field
                }}
              >
                <Textarea
                  placeholder={geoJsonPlaceholder}
                  value={field.value as string}
                  onChange={field.onChange}
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {importType === 'wkt' && (
        <FormField
          control={form.control}
          name="importData"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FormLabel>{messages.shapeImportWKTLabel} *</FormLabel>
                  <div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="flex space-x-2"
                          onClick={onChooseWKTFileClick}
                        >
                          <PiUploadThin className="h-4 w-4"></PiUploadThin>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        {messages.shapeImportWKTFile}
                      </TooltipContent>
                    </Tooltip>
                    <Input
                      ref={wktFileInputRef}
                      type="file"
                      accept=".wkt"
                      className="hidden"
                      onChange={onFileChange}
                    ></Input>
                  </div>
                </div>
                <InfoIconTooltip>
                  Import WKT via file or write it in plain text
                </InfoIconTooltip>
              </div>
              <FormControl
                onChange={(e) => {
                  form.clearErrors();
                  const target = e.target as HTMLTextAreaElement;
                  field.onChange(target.value); // set the WKT string to the field
                }}
              >
                <Textarea
                  placeholder="POLYGON ((10.5 57.75, 10.6471 57.75, 10.6471 57.8401, 10.5 57.8401, 10.5 57.75))"
                  value={field.value as string}
                  onChange={field.onChange} // Update the field value on change
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
      {importType === 'kml' && (
        <FormField
          control={form.control}
          name="importData"
          render={({ field }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-2">
                  <FormLabel>KML *</FormLabel>
                  <div>
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          className="flex space-x-2"
                          onClick={onChooseKMLFileClick}
                        >
                          <PiUploadThin className="h-4 w-4"></PiUploadThin>
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent side="top">
                        Import KML file
                      </TooltipContent>
                    </Tooltip>
                    <Input
                      ref={kmlFileInputRef}
                      type="file"
                      accept=".kml"
                      className="hidden"
                      onChange={onFileChange}
                    ></Input>
                  </div>
                </div>
                <InfoIconTooltip>
                  Import KML via file or write it in plain text
                </InfoIconTooltip>
              </div>
              <FormControl
                onChange={(e) => {
                  form.clearErrors();
                  const target = e.target as HTMLTextAreaElement;
                  field.onChange(target.value); // set the KML string to the field
                }}
              >
                <Textarea
                  placeholder={kmlPlaceholder}
                  value={field.value as string}
                  onChange={field.onChange} // Update the field value on change
                />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
      )}
    </div>
  );
};

const ImportShapeForm = ({ form }: ImportShapeFormProps) => {
  const { messages } = useLocalization();

  return (
    <ScrollArea className="flex-grow overflow-y-auto h-96 pr-4">
      <div className="space-y-8">
        <FormField
          control={form.control}
          name="name"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>{messages.shapeImportNameLabel} *</FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <Input type="text" {...field} />
              </FormControl>
              <FormMessage />
            </FormItem>
          )}
        />
        <ImportDataFormField form={form}></ImportDataFormField>
        <FormField
          control={form.control}
          name="parentId"
          render={({ field }) => (
            <FormItem>
              <div className="flex justify-between">
                <FormLabel>{messages.shapeImportShapeGroupLabel}</FormLabel>
              </div>
              <FormControl onChange={() => form.clearErrors()}>
                <GroupSelect value={field.value} onChange={field.onChange} />
              </FormControl>
            </FormItem>
          )}
        />
      </div>
    </ScrollArea>
  );
};

export function ImportShapeDialog(props: ImportShapeDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const { messages } = useLocalization();
  const { mutate } = useShapesImport();

  const form = useForm<ImportShapeValues>({
    resolver: zodResolver(importShapeSchema),
    defaultValues: {
      name: '',
      importType: 'shapefile',
      parentId: '',
      importData: undefined,
    },
  });

  const { toast } = useToast();

  const onSubmit = async (data: ImportShapeValues) => {
    setIsSubmitting(true);

    let importData: string;
    if (data.importType === 'shapefile') {
      importData = await readFileAsBase64(data.importData as File);
    } else {
      importData = data.importData as string;
    }

    const callBacks = mutateCallbacks(
      toast,
      messages,
      'create',
      'shape',
      setIsSubmitting,
      props.setShowDialog,
      {
        409: `Shape with name ${data.name} already exists.`,
      }
    );

    mutate(
      {
        name: data.name,
        importType: data.importType,
        importData: importData,
        parentId: data.parentId,
      },
      callBacks
    );
  };

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <Dialog open={props.showDialog} onOpenChange={props.setShowDialog}>
          <DialogContent id="import-shape-dialog">
            <DialogHeader>
              <DialogTitle>{messages.shapeImportTitle}</DialogTitle>
              <DialogDescription>
                {messages.shapeImportSubtitle}
              </DialogDescription>
            </DialogHeader>
            <ImportShapeForm form={form}></ImportShapeForm>
            <div className="flex items-center justify-end gap-2 pr-2 pt-6">
              <Button
                id="submit-virtual-aton-button"
                type="submit"
                onClick={form.handleSubmit(onSubmit)}
                disabled={isSubmitting}
              >
                {isSubmitting
                  ? messages.genericSubmitting
                  : messages.genericSubmit}
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </form>
    </Form>
  );
}
